import { useState } from "react";

import { cancelEntry } from "src/api/client";
import type { CancellationReason, QueueEntry } from "src/api/types";

interface Props {
  entry: QueueEntry;
  token: string;
  onCancelled: (entry: QueueEntry) => void;
  onClose: () => void;
  onError: (message: string) => void;
}

const REASONS: { value: CancellationReason; label: string }[] = [
  { value: "no_show", label: "No show" },
  { value: "unable_to_complete", label: "Unable to complete" },
];

const MAX_NOTE_LENGTH = 200;

export function CancelEntryModal({ entry, token, onCancelled, onClose, onError }: Props) {
  const [reason, setReason] = useState<CancellationReason | "">("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const noteTooLong = note.length > MAX_NOTE_LENGTH;

  async function handleConfirm() {
    if (!reason || noteTooLong) return;
    setBusy(true);
    try {
      const updated = await cancelEntry(token, entry.id, reason, note.trim() || undefined);
      onCancelled(updated);
      onClose();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-title">Cancel this entry?</h3>

        <label className="field-label" htmlFor="cancel-reason">
          Reason
        </label>
        <select
          id="cancel-reason"
          className="field-input"
          value={reason}
          onChange={(e) => setReason(e.target.value as CancellationReason)}
        >
          <option value="" disabled>
            Select a reason
          </option>
          {REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        <label className="field-label" htmlFor="cancel-note">
          Notes (optional)
        </label>
        <textarea
          id="cancel-note"
          className="field-input modal-textarea"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className={`modal-note-count${noteTooLong ? " modal-note-count-error" : ""}`}>
          {note.length}/{MAX_NOTE_LENGTH}
        </div>
        {noteTooLong && (
          <p className="modal-error">Note must be {MAX_NOTE_LENGTH} characters or fewer.</p>
        )}

        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>
            Keep
          </button>
          <button
            className="btn btn-danger"
            onClick={() => void handleConfirm()}
            disabled={!reason || noteTooLong || busy}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
